import type { BudgetBenchmark, ConversionXDeal, Decision, OntactCostInputs, OntactCostLine } from './types';
import { calculateOntactCapacity, div } from './core';

const defaultCostLines: OntactCostLine[] = [
  { id: 'rent', label: 'Rent / office', type: 'Fixed', amount: 28570, driver: 'Reference only', included: true },
  { id: 'salaries', label: 'Salaries / PAYE / admin', type: 'Fixed', amount: 78347, driver: 'Reference only', included: true },
  { id: 'accounting', label: 'Accounting / compliance', type: 'Fixed', amount: 5110, driver: 'Reference only', included: true },
  { id: 'vodacom-agents', label: 'Vodacom / agents', type: 'Variable', amount: 52427, driver: 'Reference only', included: true },
  { id: 'data', label: 'Data / enrichment', type: 'Variable', amount: 27360, driver: 'Reference only', included: true },
  { id: 'voip', label: 'VoIP / dialler', type: 'Variable', amount: 4348, driver: 'Reference only', included: true }
];

export const defaultOntactCosts: OntactCostInputs = {
  costModelMode: 'Simple',
  fixedOpexAverageAmount: 119083,
  costPerAgent: 15500,
  agentCountMode: 'Auto',
  manualAgentCount: 0,
  variableMonthlyOpex: 112480,
  onceOffOpex: 0,
  costLines: defaultCostLines,
  notes: 'Simple mode: fixed Opex average amount + cost per agent × agent count + variable Opex + once-off Opex.'
};

export function getOntactCosts(deal: ConversionXDeal): OntactCostInputs {
  const legacy = deal.ontactCosts as (OntactCostInputs & { fixedMonthlyOpex?: number }) | undefined;
  return {
    ...defaultOntactCosts,
    ...(legacy ?? {}),
    costModelMode: legacy?.costModelMode ?? defaultOntactCosts.costModelMode,
    fixedOpexAverageAmount: Number(legacy?.fixedOpexAverageAmount ?? legacy?.fixedMonthlyOpex ?? defaultOntactCosts.fixedOpexAverageAmount),
    costPerAgent: Number(legacy?.costPerAgent ?? defaultOntactCosts.costPerAgent),
    agentCountMode: legacy?.agentCountMode ?? defaultOntactCosts.agentCountMode,
    manualAgentCount: Number(legacy?.manualAgentCount ?? defaultOntactCosts.manualAgentCount),
    variableMonthlyOpex: Number(legacy?.variableMonthlyOpex ?? defaultOntactCosts.variableMonthlyOpex),
    onceOffOpex: Number(legacy?.onceOffOpex ?? defaultOntactCosts.onceOffOpex),
    costLines: legacy?.costLines?.length ? legacy.costLines : defaultOntactCosts.costLines
  };
}

export function calculateOntactCostSummary(deal: ConversionXDeal, budget: BudgetBenchmark) {
  const inputs = getOntactCosts(deal);
  const capacity = calculateOntactCapacity(deal);
  const includedLines = (inputs.costLines || []).filter((line) => line.included !== false);
  const detailedFixed = includedLines.filter((line) => line.type === 'Fixed').reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const detailedVariable = includedLines.filter((line) => line.type === 'Variable').reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const detailedOnceOff = includedLines.filter((line) => line.type === 'Once-off').reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const detailedTotal = detailedFixed + detailedVariable + detailedOnceOff;

  const autoAgentCount = Math.max(0, capacity.recommendedAgents || 0);
  const agentCount = inputs.agentCountMode === 'Manual' ? Math.max(0, Number(inputs.manualAgentCount || 0)) : autoAgentCount;
  const fixedOpexAverageAmount = Math.max(0, Number(inputs.fixedOpexAverageAmount || 0));
  const costPerAgent = Math.max(0, Number(inputs.costPerAgent || 0));
  const agentCost = costPerAgent * agentCount;
  const variableMonthlyOpex = Math.max(0, Number(inputs.variableMonthlyOpex || 0));
  const onceOffOpex = Math.max(0, Number(inputs.onceOffOpex || 0));
  const simpleTotal = fixedOpexAverageAmount + agentCost + variableMonthlyOpex + onceOffOpex;
  const hybridExtraLines = includedLines.filter((line) => line.type !== 'Fixed').reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const hybridTotal = fixedOpexAverageAmount + agentCost + variableMonthlyOpex + onceOffOpex + hybridExtraLines;
  const totalOpex = inputs.costModelMode === 'Detailed' ? detailedTotal : inputs.costModelMode === 'Hybrid' ? hybridTotal : simpleTotal;
  const costModelDescription = inputs.costModelMode === 'Detailed'
    ? 'Detailed mode uses the included reference cost lines as the active cost base.'
    : inputs.costModelMode === 'Hybrid'
      ? 'Hybrid mode uses fixed Opex and agent cost, then adds selected non-fixed reference lines.'
      : 'Simple mode uses fixed Opex average, agent cost, variable Opex and once-off Opex.';

  const tp2Results = Math.max(0, budget.funnelBudget.requiredActivations || 0);
  const tp1Results = Math.max(0, budget.funnelBudget.requiredAcceptedLeads || 0);
  const tp2Revenue = budget.entityPnl.ontact.revenue;
  const leadPurchaseCost = budget.entityPnl.ontact.leadPurchaseCost;
  const existingOntactOpsCost = budget.entityPnl.ontact.opsCost;
  const adjustedOntactCost = leadPurchaseCost + totalOpex;
  const adjustedOntactProfit = tp2Revenue - adjustedOntactCost;
  const adjustedOntactMargin = div(adjustedOntactProfit, tp2Revenue);
  const adjustedGroupCost = budget.entityPnl.offernet.cost + totalOpex;
  const adjustedGroupRevenue = budget.entityPnl.group.revenue;
  const adjustedGroupProfit = adjustedGroupRevenue - adjustedGroupCost;
  const adjustedGroupMargin = div(adjustedGroupProfit, adjustedGroupRevenue);
  const costPerTp1 = div(totalOpex, tp1Results);
  const costPerTp2 = div(totalOpex, tp2Results);
  const breakEvenTp2Payout = div(leadPurchaseCost + totalOpex, tp2Results);
  const requiredTp2Volume = Math.ceil(div(leadPurchaseCost + totalOpex, deal.commercialOffer.tp2PayoutPerActivation || 1));
  const tp2VolumeGap = Math.max(0, requiredTp2Volume - tp2Results);
  const requiredCostPerAgent = agentCount ? Math.max(0, div(tp2Revenue - leadPurchaseCost - fixedOpexAverageAmount - variableMonthlyOpex - onceOffOpex, agentCount)) : 0;
  const maxViableFixedOpex = Math.max(0, tp2Revenue - leadPurchaseCost - agentCost - variableMonthlyOpex - onceOffOpex);
  const payoutGap = Math.max(0, breakEvenTp2Payout - deal.commercialOffer.tp2PayoutPerActivation);
  const incrementalOpex = Math.max(0, totalOpex - existingOntactOpsCost);
  const adjustedCashGap = budget.cashFlow.cashGap + incrementalOpex;
  let decision: Decision = budget.decision;
  const reasons: string[] = [];

  if (tp2Revenue <= 0 && totalOpex > 0) {
    decision = 'Manual review';
    reasons.push('ONtact costs are entered but TP2 revenue is not active. Use Dual Base Model to test ONtact profitability.');
  } else {
    if (adjustedOntactProfit < 0) {
      decision = budget.decision === 'Reject' ? 'Reject' : 'Negotiate';
      reasons.push(`ONtact TP2 leg is loss-making under ${inputs.costModelMode} cost mode.`);
    }
    if (adjustedGroupMargin < deal.funnelAssumptions.minimumMargin) {
      decision = decision === 'Reject' ? 'Reject' : 'Negotiate';
      reasons.push('Group margin falls below the minimum guardrail after ONtact cost load.');
    }
    if (payoutGap > 0 && tp2Results > 0) {
      decision = decision === 'Reject' ? 'Reject' : 'Negotiate';
      reasons.push(`Increase TP2 payout by at least ${payoutGap.toFixed(0)} per result or improve volume/cost efficiency.`);
    }
  }
  if (!reasons.length) reasons.push('ONtact cost base clears the current commercial guardrails.');

  return {
    inputs,
    costModelMode: inputs.costModelMode,
    costModelDescription,
    fixedOpexAverageAmount,
    fixedMonthlyOpex: fixedOpexAverageAmount,
    costPerAgent,
    autoAgentCount,
    agentCountMode: inputs.agentCountMode,
    manualAgentCount: inputs.manualAgentCount,
    agentCount,
    agentCost,
    variableMonthlyOpex,
    onceOffOpex,
    simpleTotal,
    detailedTotal,
    hybridTotal,
    totalOpex,
    includedLines,
    referenceFixedLines: detailedFixed,
    referenceVariableLines: detailedVariable,
    fixedFromLines: detailedFixed,
    variableFromLines: detailedVariable,
    onceOffFromLines: detailedOnceOff,
    tp1Results,
    tp2Results,
    tp2Revenue,
    leadPurchaseCost,
    adjustedOntactCost,
    adjustedOntactProfit,
    adjustedOntactMargin,
    adjustedGroupCost,
    adjustedGroupRevenue,
    adjustedGroupProfit,
    adjustedGroupMargin,
    adjustedCashGap,
    costPerTp1,
    costPerTp2,
    breakEvenTp2Payout,
    requiredTp2Volume,
    tp2VolumeGap,
    requiredCostPerAgent,
    maxViableFixedOpex,
    payoutGap,
    decision,
    reasons
  };
}
